import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import "./CallReviewSheetPopup.css";
import { notify } from "../../Helper/notification";
import { downloadCallReviewPDF } from "./generateCallReviewPDF";
import { getCallReview } from "../../../operations/services/getCallReview";

/* ─── METADATA HELPERS ─── */

// Mirrors the reviewer-name lookup pattern used in AgentFeedbackModal.jsx's
// getSenderInfo() so we have a sensible default when the parent doesn't pass
// a reviewerName prop explicitly.
const getReviewerNameFromStorage = () => {
  try {
    const loginState = localStorage.getItem("loginState");
    if (!loginState) return "";
    const userData = JSON.parse(loginState)?.data?.[0];
    if (!userData) return "";
    return `${userData.FirstName || ""} ${userData.LastName || ""}`
      .replace(/\bnull\b/gi, "")
      .trim();
  } catch {
    return "";
  }
};

const formatToday = () =>
  new Date().toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });

// Formats the backend's `updatedAt` (ISO UTC) into US Eastern time to match
// "02/24/2026, 09:13:59 AM EST" — the save timestamp shown as the PDF's
// Review Date.
const formatReviewDateTime = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-US", {
    timeZone: "America/New_York",
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZoneName: "short",
  });
};

/* ─── FIELD LABELS ─── */

const USING_TOOLS_FIELDS = [
  { label: "Did the AC use the intro script?" },
  { label: "Did the AC attempt the 4 W's - What's going on?" },
  { label: "Did the AC attempt the 4 W's - How long has it been going on for?" },
  { label: "Did the AC attempt the 4 W's - Was there something that happened to start all this?" },
  { label: "Did the AC attempt the 4 W's - Why now?" },
  { label: "Was the AC in control of the call?" },
  { label: "Did AC ask for the referral source – How did you hear about us?", options: [0, 2] },
];

const SALES_FIELDS = [
  { label: "Did the AC do a good job of understanding wants and needs?" },
  { label: "Did the AC individualize a pitch on how Monte Nido's program can help with the client's wants and needs?" },
  { label: "Did the AC show empathy to the caller's situation?" },
];

const OBJECTIONS_FIELDS = [
  { label: "Did the AC initiate the assessment process on the call?" },
  { label: "Did the AC explain insurance without creating a barrier to treatment?" },
  { label: "Did the AC do a good job of overcoming objections?" },
  { label: "Did the AC create a sense of urgency?" },
  { label: "Did the AC schedule next steps?" },
];

/* ─── DEFAULT DATA ─── */

const DEFAULT_DATA = {
  usingTools: [0, 0, 0, 0, 0, 0, 0],
  sales: [0, 0, 0],
  objections: [0, 0, 0, 0, 0],
};

const DEFAULT_FEEDBACK = {
  whatWentWell: "",
  whatCanImprove: "",
  generalComments: "",
};

/* ─── SECTION BLOCK ─── */

const SectionBlock = ({
  title,
  fields,
  values,
  sectionKey,
  sectionTotal,
  onFieldChange,
}) => {
  const earned = values.reduce((sum, value) => {
  return sum + Number(value || 0);
}, 0);

  return (
    <div className="crs-block">
      <h3 className="crs-block-title">{title}</h3>

      <table className="crs-table">
        <thead>
          <tr>
            <th className="crs-th crs-th-label">CRITERIA</th>
            <th className="crs-th crs-th-pts">POINTS</th>
          </tr>
        </thead>

        <tbody>
          {fields.map((field, idx) => (
            <tr key={idx}>
              <td className="crs-td crs-td-label">{field.label}</td>
              <td className="crs-td crs-td-pts">
                <select
                  className="crs-pts-select"
                  value={values[idx] ?? 0}
                  onChange={(e) =>
                    onFieldChange(sectionKey, idx, Number(e.target.value))
                  }
                >
                  {(field.options || [0, 1]).map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

     <p className="crs-section-total">
  Section Total – {sectionTotal} points 
  <span style={{ fontWeight: 400 }}> (Earned: {earned})</span>
</p>
    </div>
  );
};

/* ─── MAIN POPUP ─── */

const CallReviewSheetPopup = ({
  isOpen,
  onClose,
  initialData,
  onSave,
  isSaving = false,
  // Metadata used to build the PDF / CSV / email summary. All optional —
  // when a backend isn't wired up yet for a given call, these simply fall
  // back to sensible client-side defaults below.
  callId,
  vonageCallId,
  magicHealthId,
  agentName,
  memberName,
  reviewerName,
  reviewDate,
  // Raw decrypted GET response (sections/totals/average/weightage/feedback)
  // for the call — Download reads straight from this, no backend call.
  reviewData,
  // Optional override for the "Email to Agent" recipient.
  agentEmail,
}) => {
  const [formData, setFormData] = useState(DEFAULT_DATA);
  const [feedbackData, setFeedbackData] = useState(DEFAULT_FEEDBACK);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isEmailing, setIsEmailing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);

  // Capture initialData in a ref so we can read its latest value inside the
  // effect without making it a dependency (avoids re-init on every render).
  const initialDataRef = useRef(initialData);
  useEffect(() => {
    initialDataRef.current = initialData;
  });

  // Initialize form state ONCE each time the popup transitions to open.
  // Intentionally omit initialData from deps — the parent creates a new object
  // reference on every render, which would cause this effect to fire and reset
  // user edits mid-session. Score edits and feedback-text edits share this
  // same guard, so editing one never resets the other while the popup is open.
  useEffect(() => {
    if (isOpen) {
      const data = initialDataRef.current;
      setFormData(data || DEFAULT_DATA);
      setFeedbackData({
        whatWentWell: data?.whatWentWell || "",
        whatCanImprove: data?.whatCanImprove || "",
        generalComments: data?.generalComments || "",
      });
      setShowShareModal(false);
      setIsEmailing(false);
      setIsDownloading(false);
      setIsDirty(false);
      setShowDiscardModal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  /* ✅ Prevent scroll */
  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  /* ✅ Calculate total */
  const totalEarned = useMemo(() => {
    return (
      formData.usingTools.reduce((a, b) => a + b, 0) +
      formData.sales.reduce((a, b) => a + b, 0) +
      formData.objections.reduce((a, b) => a + b, 0)
    );
  }, [formData]);

  if (!isOpen) return null;

  /* 🔄 Update values */
  const handleSectionChange = (sectionKey, idx, val) => {
    setIsDirty(true);
    setFormData((prev) => {
      const updated = [...prev[sectionKey]];
      updated[idx] = val;
      return { ...prev, [sectionKey]: updated };
    });
  };

  const handleFeedbackChange = (field, value) => {
    setIsDirty(true);
    setFeedbackData((prev) => ({ ...prev, [field]: value }));
  };

  /* 🚪 Safe close — prompts when unsaved changes exist */
  const handleClose = () => {
    if (isDirty) {
      setShowDiscardModal(true);
    } else {
      onClose();
    }
  };

  const handleDiscardConfirm = () => {
    setShowDiscardModal(false);
    onClose();
  };

  /* 💾 Persist scores + feedback text via the parent's onSave handler.
     `options.sendEmail`/`options.agentEmail` are forwarded straight through
     to the same encrypted POST — used by the "Email to Agent" action. */
  const persistReview = async (options) => {
    if (!onSave) return undefined;
    // Skip the save API call when nothing was edited — only exception is the
    // "Email to Agent" action, which must still hit the backend to send the email.
    if (!isDirty && !options?.sendEmail) return undefined;
    const payload = {
      ...formData,
      overallScore: totalEarned,
      whatWentWell: feedbackData.whatWentWell,
      whatCanImprove: feedbackData.whatCanImprove,
      generalComments: feedbackData.generalComments,
    };
    return onSave(payload, options);
  };

  /* Section-level max/achieved/outOf5/percentage already computed server-side
     in the GET response (reviewData.sections.UT/S/O). Falls back to a simple
     client-side achieved total when that data isn't available yet. */
  const sectionSummary = (key, values, sectionTotal) => {
    const meta = reviewData?.sections?.[key];
    if (meta) {
      return {
        max: meta.max,
        achieved: meta.achieved,
        outOf5: meta.outOf5,
        percentage: meta.percentage,
      };
    }
    return {
      max: sectionTotal,
      achieved: (values || []).reduce((s, v) => s + Number(v || 0), 0),
    };
  };

  /* Export data is built entirely from local state + the already-fetched
     `reviewData` (the decrypted GET response) — no backend call. Shared by
     both the PDF and CSV exports. */
  const buildExportData = (savedReviewDate) => ({
    callId,
    vonageCallId,
    magicHealthId,
    agentName,
    memberName,
    reviewDate:
      savedReviewDate ||
      formatReviewDateTime(reviewData?.updatedAt) ||
      reviewDate ||
      formatToday(),
    reviewerName: reviewerName || getReviewerNameFromStorage(),
    overallScore: totalEarned,
    maxScore: 16,
    overallAverage: reviewData?.average,
    sections: [
      {
        title: "Using the Tools",
        fields: USING_TOOLS_FIELDS,
        values: formData.usingTools,
        sectionTotal: 8,
        summary: sectionSummary("UT", formData.usingTools, 8),
      },
      {
        title: "Sales",
        fields: SALES_FIELDS,
        values: formData.sales,
        sectionTotal: 3,
        summary: sectionSummary("S", formData.sales, 3),
      },
      {
        title: "Objections",
        fields: OBJECTIONS_FIELDS,
        values: formData.objections,
        sectionTotal: 5,
        summary: sectionSummary("O", formData.objections, 5),
      },
    ],
    whatWentWell: feedbackData.whatWentWell,
    whatCanImprove: feedbackData.whatCanImprove,
    generalComments: feedbackData.generalComments,
  });

  /* Save button opens the Save & Share confirmation instead of saving immediately */
  const handleSaveClick = () => setShowShareModal(true);

  // When there's nothing to save, `persistReview` skips the API call and the
  // parent's onSave never fires — which is normally what closes the popup —
  // so close it here explicitly rather than relying on that side effect.
  const handleNotNow = async () => {
    setShowShareModal(false);
    await persistReview();
    onClose();
  };

  // Saves first (same encrypted POST as "Not Now" — no-op when nothing was
  // edited), then re-fetches the Call Review via the same GET the panel used
  // on load so the PDF's Review Date always reflects the latest persisted
  // `updatedAt` from the database — whether that's from a save made just now
  // or the last time this review was actually saved. Only falls back to the
  // popup's original `reviewData`/today when that re-fetch fails.
  const handleDownloadPdf = async () => {
    setIsDownloading(true);
    try {
      await persistReview();
      let savedReviewDate = "";
      try {
        const latest = await getCallReview(callId);
        savedReviewDate = formatReviewDateTime(latest?.updatedAt);
      } catch (fetchErr) {
        console.error("[CallReviewSheetPopup] failed to re-fetch call review before PDF:", fetchErr);
      }
      await downloadCallReviewPDF(buildExportData(savedReviewDate));
      notify.success("PDF downloaded successfully");
    } catch (err) {
      notify.error("Failed to generate the PDF. Please try again.");
    } finally {
      setIsDownloading(false);
    }
    setShowShareModal(false);
    onClose();
  };

  const handleEmailToAgent = async () => {
    if (!agentEmail) {
      notify.error("Unable to find email for this agent.");
      return;
    }
    setIsEmailing(true);
    try {
      await persistReview({ sendEmail: true, agentEmail });
    } finally {
      setIsEmailing(false);
    }
    setShowShareModal(false);
  };

  const isShareActionBusy = isEmailing || isDownloading || isSaving;

  return ReactDOM.createPortal(
    <>
      <div
        className="feedback-overlay"
        onClick={(e) =>
          e.target === e.currentTarget && !showShareModal && handleClose()
        }
      >
        <div
          className="crs-popup-content"
          onClick={(e) => e.stopPropagation()}
        >
          {/* HEADER */}
          <div className="crs-popup-header">
            <h2 className="crs-main-title">Call Review Sheet</h2>
          </div>

          {/* BODY */}
          <div className="crs-body">
            <SectionBlock
              title="Using the Tools"
              fields={USING_TOOLS_FIELDS}
              values={formData.usingTools}
              sectionKey="usingTools"
              sectionTotal={8}
              onFieldChange={handleSectionChange}
            />

            <SectionBlock
              title="Sales"
              fields={SALES_FIELDS}
              values={formData.sales}
              sectionKey="sales"
              sectionTotal={3}
              onFieldChange={handleSectionChange}
            />

            <SectionBlock
              title="Objections"
              fields={OBJECTIONS_FIELDS}
              values={formData.objections}
              sectionKey="objections"
              sectionTotal={5}
              onFieldChange={handleSectionChange}
            />

            {/* TOTAL */}
            <div className="crs-overall-row">
              <span style={{ fontWeight: 700 }}>
                Overall Score:
              </span>
              <input
                type="text"
                className="crs-pts-select"
                style={{ width: "50px", textAlign: "center" }}
                value={totalEarned}
                readOnly
              />
              <span>/ 16 points</span>
            </div>
            <p
              className="crs-score-disclaimer"
              style={{
                margin: "6px 0 0",
                fontSize: "12px",
                fontStyle: "italic",
                color: "#8a6d1a",
              }}
            >
              ! Note: The overall score is not calculated using weightage
              factors.
            </p>

            {/* FEEDBACK */}
            <div className="crs-block">
              <h3 className="crs-block-title">Reviewer Feedback</h3>

              <div className="crs-feedback-stack">
                <div className="crs-feedback-group">
                  <label className="crs-feedback-label" htmlFor="crs-went-well">
                    What Went Well
                  </label>
                  <textarea
                    id="crs-went-well"
                    className="crs-feedback-textarea"
                    placeholder="Highlight what the agent did well on this call..."
                    value={feedbackData.whatWentWell}
                    onChange={(e) => handleFeedbackChange("whatWentWell", e.target.value)}
                  />
                </div>

                <div className="crs-feedback-group">
                  <label className="crs-feedback-label" htmlFor="crs-can-improve">
                    What Can Improve
                  </label>
                  <textarea
                    id="crs-can-improve"
                    className="crs-feedback-textarea"
                    placeholder="Note areas the agent could improve on..."
                    value={feedbackData.whatCanImprove}
                    onChange={(e) => handleFeedbackChange("whatCanImprove", e.target.value)}
                  />
                </div>

                <div className="crs-feedback-group">
                  <label className="crs-feedback-label" htmlFor="crs-general-comments">
                    General Comments
                  </label>
                  <textarea
                    id="crs-general-comments"
                    className="crs-feedback-textarea"
                    placeholder="Any other comments about this call..."
                    value={feedbackData.generalComments}
                    onChange={(e) => handleFeedbackChange("generalComments", e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* FOOTER */}
          <div className="crs-footer">
            <span className="feedback-cancel-text" onClick={handleClose}>
              Cancel
            </span>

            <button
              onClick={handleSaveClick}
              disabled={isSaving}
              className="crs-save-btn"
            >
              {isSaving ? "Saving..." : "Save and Send"}
            </button>
          </div>
        </div>
      </div>

      {/* SAVE & SHARE CONFIRMATION */}
      {showShareModal && (
        <div
          className="crs-share-overlay"
          onClick={(e) => e.target === e.currentTarget && !isShareActionBusy && setShowShareModal(false)}
        >
          <div className="crs-share-modal" onClick={(e) => e.stopPropagation()}>
            <p className="crs-share-title">
              Would you like to share this feedback with the agent?
            </p>

            <div className="crs-share-actions">
              <button
                className="crs-share-btn crs-share-btn-primary"
                onClick={handleEmailToAgent}
                disabled={isShareActionBusy || !agentEmail}
                title={!agentEmail ? "Unable to find email for this agent." : undefined}
              >
                {isEmailing ? (
                  <>
                    <span className="crs-share-loader" /> Sending...
                  </>
                ) : (
                  "Email to Agent"
                )}
              </button>

              <button
                className="crs-share-btn crs-share-btn-secondary"
                onClick={handleDownloadPdf}
                disabled={isShareActionBusy}
              >
                {isDownloading ? (
                  <>
                    <span className="crs-share-loader crs-share-loader-dark" /> Generating...
                  </>
                ) : (
                  "Download PDF"
                )}
              </button>

              <button
                className="crs-share-btn crs-share-btn-ghost"
                onClick={handleNotNow}
                disabled={isShareActionBusy}
              >
                Not Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DISCARD CHANGES CONFIRMATION */}
      {showDiscardModal && (
        <div className="crs-share-overlay">
          <div className="crs-share-modal" onClick={(e) => e.stopPropagation()}>
            <p className="crs-share-title">
              You have unsaved changes. Are you sure you want to discard them?
            </p>
            <div className="crs-share-actions">
              <button
                className="crs-share-btn crs-share-btn-primary"
                onClick={handleDiscardConfirm}
              >
                Discard Changes
              </button>
              <button
                className="crs-share-btn crs-share-btn-ghost"
                onClick={() => setShowDiscardModal(false)}
              >
                Keep Editing
              </button>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
};

export default CallReviewSheetPopup;